import { FC, useEffect, useState } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  TextField,
  Typography,
} from '@mui/material';
import { api } from 'src/utils/api';
import { getToken } from 'src/utils/localStorage';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { useSnackbar } from 'src/providers/snackbar/hook/useSnackbar';

const emerald = '#10b981';
const navy = '#061829';

type OnboardingRequestModalProps = {
  open: boolean;
  onClose: () => void;
  storeHint: string | null;
};

export const OnboardingRequestModal: FC<OnboardingRequestModalProps> = ({
  open,
  onClose,
  storeHint,
}) => {
  const { user } = useAccount();
  const snackbar = useSnackbar();
  const [email, setEmail] = useState('');
  const [store, setStore] = useState('');
  const [preferredTimes, setPreferredTimes] = useState('');
  const [goal, setGoal] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    setEmail(user?.email || '');
    setStore(storeHint ? `${storeHint}.myshopify.com` : '');
    setPreferredTimes('');
    setGoal('');
  }, [open, user?.email, storeHint]);

  const canSubmit =
    email.trim().includes('@') &&
    preferredTimes.trim().length >= 3 &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    const token = getToken();
    if (!token) {
      snackbar('Please log in again to send your request.', 'error');
      return;
    }

    setSubmitting(true);
    try {
      await api.post(
        '/onboarding-request',
        {
          email: email.trim(),
          store: store.trim() || undefined,
          preferredTimes: preferredTimes.trim(),
          goal: goal.trim() || undefined,
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      snackbar(
        'Request sent! We’ll email you soon to book your free onboarding.',
        'success',
      );
      onClose();
    } catch (error: unknown) {
      const status =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { status?: number; data?: { error?: string } } })
              .response?.status
          : undefined;
      const apiError =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response
              ?.data?.error
          : undefined;
      if (status === 401) {
        snackbar('Session expired. Please log in again.', 'error');
      } else {
        snackbar(apiError || 'Could not send onboarding request.', 'error');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={submitting ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'hidden',
        },
      }}
    >
      <Box
        sx={{
          bgcolor: navy,
          color: '#fff',
          px: { xs: 2.5, sm: 3 },
          py: 2.25,
        }}
      >
        <Typography sx={{ fontWeight: 800, fontSize: '1.15rem', mb: 0.5 }}>
          Book free onboarding
        </Typography>
        <Typography sx={{ color: 'rgba(255,255,255,0.75)', fontSize: 13 }}>
          Limited-time session — tell us when you&apos;re free and we&apos;ll
          follow up.
        </Typography>
      </Box>

      <DialogContent sx={{ px: { xs: 2.5, sm: 3 }, py: 2.5 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Your email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            fullWidth
            size="small"
            helperText="We’ll reply to this address"
          />
          <TextField
            label="Store / site"
            value={store}
            onChange={(e) => setStore(e.target.value)}
            fullWidth
            size="small"
            placeholder="yourstore.myshopify.com"
          />
          <TextField
            label="Best times (with timezone)"
            value={preferredTimes}
            onChange={(e) => setPreferredTimes(e.target.value)}
            required
            fullWidth
            size="small"
            placeholder="e.g. Tue–Thu 10:00–13:00 CET"
            helperText="Required — so we can propose a slot quickly"
          />
          <TextField
            label="What should we focus on? (optional)"
            value={goal}
            onChange={(e) => setGoal(e.target.value)}
            fullWidth
            size="small"
            multiline
            minRows={2}
            placeholder="Finance setup, ads, weekly email reports…"
          />

          <Box
            sx={{
              display: 'flex',
              gap: 1.25,
              justifyContent: 'flex-end',
              mt: 0.5,
            }}
          >
            <Button
              onClick={onClose}
              disabled={submitting}
              sx={{ textTransform: 'none', fontWeight: 600 }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit}
              disabled={!canSubmit}
              sx={{
                textTransform: 'none',
                fontWeight: 700,
                bgcolor: emerald,
                '&:hover': { bgcolor: '#0e9f6e' },
                minWidth: 140,
              }}
            >
              {submitting ? (
                <CircularProgress size={20} sx={{ color: '#fff' }} />
              ) : (
                'Send request'
              )}
            </Button>
          </Box>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default OnboardingRequestModal;
